import { useEffect, useRef, useState } from 'react';
import { useT } from '../i18n/translations';
import { API_BASE } from '../authApi';
import { uploadChatFile } from '../meetingRoomApi';
import './MeetingChat.css';

const IconSend = () => (
  <svg viewBox="0 0 24 24"><path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z"/></svg>
);
const IconAttach = () => (
  <svg viewBox="0 0 24 24"><path d="M16.5 6v11.5c0 2.21-1.79 4-4 4s-4-1.79-4-4V5a2.5 2.5 0 0 1 5 0v10.5c0 .55-.45 1-1 1s-1-.45-1-1V6H10v9.5a2.5 2.5 0 0 0 5 0V5c0-2.21-1.79-4-4-4S7 2.79 7 5v12.5c0 3.04 2.46 5.5 5.5 5.5s5.5-2.46 5.5-5.5V6h-1.5z"/></svg>
);
const IconImage = () => (
  <svg viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
);
const IconFile = () => (
  <svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>
);
const IconDownload = () => (
  <svg viewBox="0 0 24 24"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
);
const IconClose = () => (
  <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
);
const IconChat = () => (
  <svg viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
);

function formatTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * MeetingChat
 * Props:
 *   open        boolean  — whether the panel is visible
 *   onClose     fn
 *   messages    array    — [{ id, senderId, senderName, type, content, fileName, fileSize, sentAt }]
 *   myUserId    string
 *   onSend      fn(type, content, fileName, fileSize)
 *   unread      number
 */
export default function MeetingChat({ open, onClose, messages, myUserId, onSend, unread }) {
  const t = useT();
  const [text, setText]           = useState('');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview]     = useState(null); // { url } for lightbox
  const bottomRef  = useRef(null);
  const fileRef    = useRef(null);
  const imageRef   = useRef(null);
  const textareaRef = useRef(null);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  async function handleSendText(e) {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    setText('');
    await onSend('text', trimmed);
    textareaRef.current?.focus();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendText();
    }
  }

  async function handleFileChange(e, forceImage = false) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setUploading(true);
    try {
      const result = await uploadChatFile(file);
      const type = forceImage ? 'image' : result.type;
      await onSend(type, result.url, result.fileName, result.fileSize);
    } catch (err) {
      console.error('Chat upload failed:', err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      {/* Lightbox */}
      {preview && (
        <div className="mchat__lightbox" onClick={() => setPreview(null)}>
          <button className="mchat__lightbox-close" onClick={() => setPreview(null)}>
            <IconClose />
          </button>
          <img src={preview.url} alt="preview" onClick={e => e.stopPropagation()} />
        </div>
      )}

      <div className={`mchat${open ? ' mchat--open' : ''}`}>
        {/* Header */}
        <div className="mchat__header">
          <div className="mchat__header-left">
            <IconChat />
            <span>{t('chat.title')}</span>
            {unread > 0 && !open && (
              <span className="mchat__badge">{unread}</span>
            )}
          </div>
          <button className="mchat__close-btn" onClick={onClose} title={t('common.close')}>
            <IconClose />
          </button>
        </div>

        {/* Message list */}
        <div className="mchat__messages">
          {messages.length === 0 && (
            <div className="mchat__empty">
              <IconChat />
              <p>{t('chat.empty')}</p>
            </div>
          )}

          {messages.map((msg, i) => {
            const isMe = msg.senderId === myUserId;
            const showName = !isMe && (i === 0 || messages[i - 1].senderId !== msg.senderId);
            const fileUrl = msg.content?.startsWith('http') ? msg.content : `${API_BASE}${msg.content}`;

            return (
              <div key={msg.id} className={`mchat__msg-row${isMe ? ' mchat__msg-row--me' : ''}`}>
                {!isMe && (
                  <div className="mchat__avatar">
                    {(msg.senderName || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="mchat__bubble-wrap">
                  {showName && (
                    <span className="mchat__sender">{msg.senderName}</span>
                  )}
                  <div className={`mchat__bubble${isMe ? ' mchat__bubble--me' : ''}`}>
                    {msg.type === 'text' && (
                      <p className="mchat__text">{msg.content}</p>
                    )}
                    {msg.type === 'image' && (
                      <img
                        className="mchat__img"
                        src={fileUrl}
                        alt={msg.fileName || 'image'}
                        onClick={() => setPreview({ url: fileUrl })}
                      />
                    )}
                    {msg.type === 'file' && (
                      <a className="mchat__file" href={fileUrl} target="_blank" rel="noreferrer" download={msg.fileName}>
                        <div className="mchat__file-icon"><IconFile /></div>
                        <div className="mchat__file-info">
                          <span className="mchat__file-name">{msg.fileName || t('chat.file')}</span>
                          <span className="mchat__file-size">{formatFileSize(msg.fileSize)}</span>
                        </div>
                        <div className="mchat__file-dl"><IconDownload /></div>
                      </a>
                    )}
                    <span className="mchat__time">{formatTime(msg.sentAt)}</span>
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Input bar */}
        <div className="mchat__input-bar">
          {uploading && (
            <div className="mchat__uploading">
              <span className="mchat__upload-spinner" />
              <span>{t('chat.uploading')}</span>
            </div>
          )}
          <div className="mchat__input-row">
            {/* Image picker */}
            <button
              className="mchat__tool-btn"
              title={t('chat.sendImage')}
              onClick={() => imageRef.current?.click()}
              disabled={uploading}
            >
              <IconImage />
            </button>
            <input
              ref={imageRef}
              type="file"
              accept="image/*"
              hidden
              onChange={e => handleFileChange(e, true)}
            />

            {/* File picker */}
            <button
              className="mchat__tool-btn"
              title={t('chat.sendFile')}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <IconAttach />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="*/*"
              hidden
              onChange={e => handleFileChange(e, false)}
            />

            {/* Text input */}
            <textarea
              ref={textareaRef}
              className="mchat__textarea"
              placeholder={t('chat.placeholder')}
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={uploading}
            />

            {/* Send */}
            <button
              className="mchat__send-btn"
              onClick={handleSendText}
              disabled={!text.trim() || uploading}
              title={t('chat.send')}
            >
              <IconSend />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
